<template>
  <div>
    <!-- BEGIN PAGE BAR -->
    <div class="page-bar">
      <breadcrumb :crumbs="breadcrumbArray" />
    </div>
    <!-- END PAGE BAR -->
    <!-- BEGIN PAGE TITLE-->
    <h1 class="page-title">
      News Feed
    </h1>
    <!-- END PAGE TITLE-->
    <div class="note note-info">
      <p>Create and manage the Application News Feed.</p>
    </div>
    <!-- CREATE START -->
    <div
      v-if="$root.permissions['news_feed create']"
      class="portlet box blue-hoki margin-top-20"
    >
      <div
        class="portlet-title bg-blue-chambray"
        @click="toggleCreateFeedPanel()"
      >
        <div class="custom tools">
          <a :class="{'expand': !createFeedCollapse, 'collapse': createFeedCollapse}" />
        </div>
        <div class="caption">
          &emsp;Create News Feed
        </div>
      </div>
      <div
        class="portlet-body"
        :class="{'display-hide': createFeedCollapse}"
      >
        <form
          role="form"
          @submit.prevent="createNewsFeed($event)"
        >
          <div class="row">
            <div class="col-md-12">
              <div
                v-show="createFeedError.length"
                ref="createFeedError"
                class="alert alert-danger"
              >
                <button
                  class="close"
                  data-close="alert"
                  @click.prevent="clearCreateFeedError()"
                />
                <span>{{ createFeedError }}</span>
              </div>
            </div>
            <div :class="{'col-md-2' : !imageMode.newMenu, 'col-md-12' : imageMode.newMenu}">
              <resource-picker
                :image-button="true"
                :image-url="newNewsFeed.image"
                class="margin-top-15"
                @open="toggleImageMode('newMenu', true)"
                @close="toggleImageMode('newMenu', false)"
                @selected="updateImage"
              />
            </div>
            <div
              v-show="!imageMode.newMenu"
              class="col-md-5"
            >
              <div class="form-group form-md-line-input form-md-floating-label">
                <input
                  id="form_control_1"
                  v-model="newNewsFeed.title"
                  type="text"
                  class="form-control input-sm"
                  :class="{'edited': newNewsFeed.title.length}"
                >
                <label for="form_control_1">
                  Title
                </label>
              </div>
              <div class="form-group form-md-line-input form-md-floating-label">
                <input
                  id="form_control_2"
                  v-model="newNewsFeed.short_description"
                  type="text"
                  class="form-control input-sm"
                  :class="{'edited': newNewsFeed.short_description.length}"
                >
                <label for="form_control_2">
                  Short Description
                </label>
              </div>
              <div class="form-group form-md-line-input form-md-floating-label">
                <input
                  id="form_control_external_url"
                  v-model="newNewsFeed.external_url"
                  type="text"
                  class="form-control input-sm"
                  :class="{'edited': newNewsFeed.external_url.length}"
                >
                <label for="form_control_2">
                  Link
                </label>
              </div>
            </div>
            <div
              v-show="!imageMode.newMenu"
              class="col-md-5"
            >
              <div class="form-group form-md-line-input form-md-floating-label">
                <textarea
                  v-model="newNewsFeed.body"
                  class="form-control input-sm"
                  rows="4"
                  :class="{'edited': newNewsFeed.body.length}"
                />
                <label for="form_control_1">
                  Body
                </label>
              </div>
            </div>
            <div
              v-show="!imageMode.newMenu"
              class="col-xs-12"
            >
              <div class="pull-right">
                <button
                  type="submit"
                  class="btn blue"
                  :disabled="creating"
                >
                  Save
                  <i
                    v-show="creating"
                    class="fa fa-spinner fa-pulse fa-fw"
                  />
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
    <!-- CREATE END -->
    <loading-screen
      v-if="displayNewsFeedData"
      :show="displayNewsFeedData"
      :color="'#2C3E50'"
      :display="'inline'"
    />
    <template v-else>
      <no-results
        v-if="!newsFeed.length"
        :show="!newsFeed.length"
        :type="'news feed'"
      />
      <div
        v-else
        class="margin-top-20"
      >
        <div class="relative-block">
          <div
            v-if="newsFeed.length"
            class="clearfix"
          >
            <el-dropdown
              trigger="click"
              size="mini"
              :show-timeout="50"
              :hide-timeout="50"
              @command="updateSortByOrder"
            >
              <el-button size="mini">
                Sort by
                <span>
                  <i
                    v-if="sortBy.order === 'ASC'"
                    class="fa fa-sort-alpha-asc"
                  />
                  <i
                    v-if="sortBy.order === 'DESC'"
                    class="fa fa-sort-alpha-desc"
                  />
                </span>
                <i class="el-icon-arrow-down el-icon--right" />
              </el-button>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item command="ASC">
                  <i class="fa fa-sort-alpha-asc" />
                </el-dropdown-item>
                <el-dropdown-item command="DESC">
                  <i class="fa fa-sort-alpha-desc" />
                </el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
            <page-results
              class="pull-right"
              :total-results="totalResults"
              :active-page="activePage"
              @pageResults="pageResultsUpdate"
            />
          </div>
          <div class="portlet light portlet-fit bordered margin-top-20">
            <div class="portlet-title">
              <div class="caption">
                <i class="fa fa-newspaper-o font-blue-madison" />
                <span class="caption-subject bold font-blue-madison uppercase">
                  News Feed
                </span>
              </div>
            </div>
            <div class="portlet-body">
              <div class="timeline">
                <!-- TIMELINE ITEM -->
                <div
                  v-for="(news, index) in newsFeed"
                  :key="index"
                  class="timeline-item"
                >
                  <div class="timeline-badge">
                    <img
                      v-if="news.image.length"
                      class="timeline-badge-userpic"
                      :src="news.image"
                    >
                    <img
                      v-if="!news.image.length"
                      class="timeline-badge-userpic"
                      src="../../../src/assets/img/app/image-placeholder.png"
                    >
                  </div>
                  <div
                    :id="'news-' + news.id"
                    class="timeline-body"
                  >
                    <div class="timeline-body-arrow" />
                    <div class="timeline-body-head">
                      <div class="timeline-body-head-caption">
                        <a class="timeline-body-title font-blue-madison">
                          {{ news.title }}
                        </a>
                      </div>
                      <div class="timeline-body-head-actions">
                        <div
                          v-if="$root.permissions['news_feed update']"
                          class="btn-group"
                        >
                          <button
                            class="btn blue btn-sm"
                            type="button"
                            @click="editNewsFeed(news)"
                          >
                            Edit
                          </button>
                        </div>
                        <div
                          v-if="$root.permissions['news_feed read'] && !$root.permissions['news_feed update']"
                          class="btn-group"
                        >
                          <button
                            class="btn blue btn-sm"
                            type="button"
                            @click="editNewsFeed(news)"
                          >
                            View
                          </button>
                        </div>
                        <div
                          v-if="$root.permissions['news_feed delete']"
                          class="btn-group"
                        >
                          <button
                            class="btn blue btn-outline btn-sm"
                            type="button"
                            @click="openDeleteModal(news)"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                    <div class="timeline-body-content">
                      <h5>{{ news.formatted_date }}</h5>
                      <span class="font-grey-cascade">
                        {{ news.short_description }}
                      </span>
                      <span class="font-grey-cascade">
                        {{ news.external_url }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <!-- END TIMELINE ITEM -->
            </div>
            <div
              v-show="!newsFeed && errorMessage"
              ref="errorMessage"
              class="alert alert-danger"
            >
              <button
                class="close"
                @click="clearError('errorMessage')"
              />
              <span>{{ errorMessage }}</span>
            </div>
          </div>
          <pagination
            v-if="newsFeed.length && numPages > 1"
            :passed-page="activePage"
            :num-pages="numPages"
            @activePageChange="activePageUpdate"
          />
        </div>
      </div>
    </template>

    <edit-news-feed
      v-if="showEditFeedModal"
      :selected-feed-id="selectedFeedId"
      @closeEditFeedModal="closeEditFeedModal"
      @updateNewsFeed="updateNewsFeed"
    />

    <!-- START DELETE -->
    <modal
      ref="deleteModal"
      :show="showDeleteModal"
      effect="fade"
      @closeOnEscape="closeDeleteModal"
    >
      <div
        slot="modal-header"
        class="modal-header"
      >
        <button
          type="button"
          class="close"
          @click="closeDeleteModal()"
        >
          <span>&times;</span>
        </button>
        <h4 class="modal-title center">
          Confirm Delete
        </h4>
      </div>
      <div
        slot="modal-body"
        class="modal-body"
      >
        <div
          v-show="deleteErrorMessage"
          ref="deleteErrorMessage"
          class="alert alert-danger"
        >
          <button
            class="close"
            @click="clearError('deleteErrorMessage')"
          />
          <span>{{ deleteErrorMessage }}</span>
        </div>

        <p>Are you sure you want to delete the news feed?</p>
      </div>
      <div
        slot="modal-footer"
        class="modal-footer clear"
      >
        <button
          type="button"
          class="btn blue"
          :disabled="deleting"
          @click="deleteNewsFeed()"
        >
          Delete
          <i
            v-show="deleting"
            class="fa fa-spinner fa-pulse fa-fw"
          />
        </button>
      </div>
    </modal>
    <!-- START DELETE -->
  </div>
</template>

<script>
import Breadcrumb from '../modules/Breadcrumb'
import NoResults from '../modules/NoResults'
import PageResults from '../modules/PageResults'
import Modal from '@/components/modules/Modal'
import Pagination from '../modules/Pagination'
import Dropdown from '../modules/Dropdown'
import NewsFeedFunctions from '../../controllers/NewsFeed'
import EditNewsFeed from './NewsFeed/EditNewsFeed'
import ResourcePicker from '../modules/ResourcePicker'
import ajaxErrorHandler from '@/controllers/ErrorController'
import LoadingScreen from '@/components/modules/LoadingScreen'

export default {
  components: {
    Breadcrumb,
    PageResults,
    Pagination,
    Dropdown,
    EditNewsFeed,
    NoResults,
    ResourcePicker,
    Modal,
    LoadingScreen
  },
  data () {
    return {
      breadcrumbArray: [{ name: 'News Feed', link: false }],
      sortBy: {
        order: 'ASC'
      },
      pageResultsValue: 0,
      numPages: 0,
      totalResults: 0,
      activePage: 1,
      newsFeed: [],
      errorMessage: '',
      createFeedCollapse: true,
      newNewsFeed: {
        title: '',
        short_description: '',
        body: '',
        image: '',
        external_url: ''
      },
      createFeedError: '',
      creating: false,
      showEditFeedModal: false,
      updateFeedError: '',
      selectedFeedId: 0,
      imageMode: {
        newMenu: false
      },
      showDeleteModal: false,
      newsToDelete: {},
      deleting: false,
      deleteErrorMessage: '',
      displayNewsFeedData: false
    }
  },
  watch: {
    'sortBy.order' () {
      if (this.sortBy.order) {
        this.activePageUpdate(1)
        this.getNewsFeed(0)
      }
    }
  },
  created () {
    this.getNewsFeed(0)
  },
  methods: {
    /**
     * To close the modal for deleting a promotion and remove that promotion from DOM.
     * @function
     * @returns {undefined}
     */
    deleteNewsFeed () {
      this.deleting = true
      var _this = this
      return NewsFeedFunctions.deleteNewsFeed(_this.newsToDelete.id)
        .then(response => {
          if (response.code === 200 && response.status === 'ok') {
            _this.getNewsFeed(_this.activePage)
            _this.closeDeleteModal()
            _this.showDeleteSuccess(response.payload)
          }
        })
        .catch(reason => {
          ajaxErrorHandler({
            reason,
            errorText: `We could not delete the news feed`,
            errorName: 'deleteErrorMessage',
            vue: _this,
            containerRef: 'deleteModal'
          })
        })
        .finally(() => {
          _this.deleting = false
        })
    },
    /**
     * To notify user of the outcome of the call
     * @function
     * @param {object} payload - The payload object from the server response
     * @returns {undefined}
     */
    showDeleteSuccess (payload = {}) {
      let title = 'Success'
      let text = 'The News Feed has been deleted'
      let type = 'success'

      if (payload && payload.pending_approval) {
        title = 'Approval Required'
        text = 'The removal has been sent for approval'
        type = 'info'
      }

      this.$swal({
        title,
        text,
        type
      })
    },
    /**
     * To clear an error
     * @function
     * @param {string} name - Name of the variable to clear
     * @returns {undefined}
     */
    clearError (name) {
      this[name] = ''
    },
    /**
     * To show the delete modal
     * @function
     * @param {object} news - The news object to delete
     * @returns {undefined}
     */
    openDeleteModal (news) {
      this.newsToDelete = { ...news }
      this.showDeleteModal = true
    },
    closeDeleteModal () {
      this.clearError('deleteErrorMessage')
      this.showDeleteModal = false
    },
    /**
     * To toggle between the open and closed state of the resource picker
     * @function
     * @param {string} object - The name of the object the image is for
     * @param {object} value - The open / closed value of the picker
     * @returns {undefined}
     */
    toggleImageMode (object, value) {
      this.imageMode[object] = value
    },
    /**
     * To set the image to be same as the one emitted by the resource picker.
     * @function
     * @param {object} val - The emitted image object.
     * @returns {undefined}
     */
    updateImage (val) {
      this.newNewsFeed.image = val.image_url
    },
    /**
     * To update the order property of sortBy.
     * @function
     * @param {object} value - The new value to assign.
     * @returns {undefined}
     */
    updateSortByOrder (value) {
      this.sortBy.order = value
    },
    /**
     * To catch updates from the PageResults component when the number of page results is updated.
     * @function
     * @param {integer} val - The number of page results to be returned.
     * @returns {undefined}
     */
    pageResultsUpdate (val) {
      if (parseInt(this.pageResultsValue) !== parseInt(val)) {
        this.pageResultsValue = val
        this.activePageUpdate(1)
        this.getNewsFeed(0)
      }
    },
    /**
     * To update the currently active pagination page.
     * @function
     * @param {integer} val - An integer representing the page number that we are updating to.
     * @returns {undefined}
     */
    activePageUpdate (val) {
      if (parseInt(this.activePage) !== parseInt(val)) {
        this.activePage = val
        this.getNewsFeed(this.activePage)
      }
    },
    /**
     * To get the news feed for the current page.
     * @function
     * @param {integer} pageNumber - The current page that we are retrieving results for.
     * @returns {object} - A promise that will either return an error message or perform an action.
     */
    getNewsFeed (pageNumber) {
      this.displayNewsFeedData = true
      this.newsFeed = []
      var newsFeedVue = this
      return NewsFeedFunctions.getNewsFeed({
        page: pageNumber,
        order: newsFeedVue.sortBy.order,
        itemsPerPage: newsFeedVue.pageResultsValue
      })
        .then(response => {
          if (response.code === 200 && response.status === 'ok') {
            newsFeedVue.totalResults = response.payload.data.length
            newsFeedVue.newsFeed = response.payload.data
            for (var i = 0; i < newsFeedVue.newsFeed.length; i++) {
              var item = newsFeedVue.newsFeed[i].created_on.split(' ')
              if (item[0] !== '0000-00-00') {
                var date = new Date(item[0])
                var dateSplit = date.toString().split(' ')
                newsFeedVue.$set(
                  newsFeedVue.newsFeed[i],
                  'formatted_date',
                  dateSplit[1] + ' ' + dateSplit[2] + ', ' + dateSplit[3]
                )
              } else {
                newsFeedVue.$set(
                  newsFeedVue.newsFeed[i],
                  'formatted_date',
                  'Invalid Date'
                )
              }
            }
          } else {
            newsFeedVue.errorMessage = response.message
          }
        })
        .catch(reason => {
          ajaxErrorHandler({
            reason,
            errorText: 'We could not fetch news feeds',
            errorName: 'errorMessage',
            vue: newsFeedVue
          })
        }).finally(() => {
          newsFeedVue.displayNewsFeedData = false
        })
    },
    /**
     * To toggle the create feed panel, initially set to closed
     * @function
     * @returns {undefined}
     * @memberof Users
     * @version 0.0.4
     */
    toggleCreateFeedPanel () {
      this.createFeedCollapse = !this.createFeedCollapse
    },
    /**
     * To clear the current search error.
     * @function
     * @returns {undefined}
     * @memberof Users
     * @version 0.0.4
     */
    clearCreateFeedError () {
      this.createFeedError = ''
    },
    /**
     * To clear the create feed form.
     * @function
     * @returns {undefined}
     * @memberof Users
     * @version 0.0.4
     */
    resetForm () {
      this.newNewsFeed = {
        title: '',
        short_description: '',
        body: '',
        image: '',
        external_url: ''
      }
      this.clearCreateFeedError()
    },
    /**
     * To check if the news feed information are valid before submitting to the backend.
     * @function
     * @returns {object} A promise that will validate the input form
     */
    validateNewsFeedData () {
      var newsFeedVue = this
      return new Promise(function (resolve, reject) {
        if (!newsFeedVue.newNewsFeed.title.length) {
          reject('Title cannot be blank')
        } else if (!newsFeedVue.newNewsFeed.short_description.length) {
          reject('Short description cannot be blank')
        } else if (!newsFeedVue.newNewsFeed.body.length) {
          reject('Body cannot be blank')
        } else if (!newsFeedVue.newNewsFeed.image.length) {
          reject('Image cannot be blank')
        }
        resolve('Hurray')
      })
    },
    /**
     * To prompt the backend call that creates a new news feed.
     * @function
     * @param {object} event - The click event that prompted this function.
     * @returns {object} A promise that will either return an error message or display the success screen
     */
    createNewsFeed (event) {
      var newsFeedVue = this

      this.clearCreateFeedError()
      return newsFeedVue
        .validateNewsFeedData()
        .then(response => {
          newsFeedVue.creating = true
          NewsFeedFunctions.createNewsFeed(
            newsFeedVue.newNewsFeed,
            newsFeedVue.$root.userToken,
            newsFeedVue.$root.appId,
            newsFeedVue.$root.appSecret
          )
            .then(response => {
              if (response.code === 200 && response.status === 'ok') {
                newsFeedVue.getNewsFeed()
                newsFeedVue.showCreateSuccess(response.payload)
                newsFeedVue.resetForm()
              } else {
                newsFeedVue.createFeedError = response.message
              }
            })
            .catch(reason => {
              console.log({ reason })
              ajaxErrorHandler({
                reason,
                errorText: 'Could not create news feed',
                errorName: 'createFeedError',
                vue: newsFeedVue
              })
            })
            .finally(() => {
              newsFeedVue.creating = false
            })
        })
        .catch(reason => {
          // If validation fails then display the error message
          newsFeedVue.createFeedError = reason
          window.scrollTo(0, 0)
          throw reason
        })
    },
    /**
     * To notify user of the outcome of the call
     * @function
     * @param {object} payload - The payload object from the server response
     * @returns {undefined}
     */
    showCreateSuccess (payload = {}) {
      let title = 'Success'
      let text = 'The News Feed has been created'
      let type = 'success'

      if (payload && payload.pending_approval) {
        title = 'Approval Required'
        text = 'The News Feed has been sent for approval'
        type = 'info'
      }

      this.$swal({
        title,
        text,
        type
      })
    },
    /**
     * To show input fields instead of the plain text for news feed.
     * @function
     * @param {object} news - The news object to be edited
     * @returns {undefined}
     */
    editNewsFeed (news) {
      this.showEditFeedModal = true
      this.selectedFeedId = news.id
    },
    /**
     * To close the edit feed modal and get the updated list of news feed
     * @function
     * @returns {undefined}
     */
    closeEditFeedModal () {
      this.showEditFeedModal = false
    },
    updateNewsFeed (val) {
      this.showEditFeedModal = false
      this.getNewsFeed()
    }
  }
}
</script>
<style scoped>
.image-wrapper {
  height: 80px;
  max-height: 80px;
  width: 100%;
  border: 1px dotted #c2cad8;
  display: flex;
  justify-content: center;
  align-items: center;
}
.image-fit {
  max-height: 100%;
  max-width: 100%;
}
</style>
